import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiReports } from "../api/reports";
import { loadGoogle } from "./InspectorRouteMap";

const EXAMPLES = [
  "New inspections in Florida",
  "Inspections assigned to Smith in Charleston",
  "No-access inspections in Texas",
];

const apiErrorMessage = (data, fallback) => data?.errors?.[0]?.message || data?.message || fallback;
const REPORT_COLUMNS = [
  ["Policy #", "policyNo"], ["Address", "address"], ["City, State", "location"],
  ["Age", "ageDays"], ["Type", "inspectionType"], ["Appointment", "appointment"],
  ["Status", "prettyStatus"], ["Inspector", "inspector"], ["Policy Status", "policyStatus"],
];

const AGE_LEGEND = [
  ["#16a34a", "0-7d"], ["#a3d977", "8-14d"], ["#fbbf24", "15-21d"],
  ["#f97316", "22-30d"], ["#ef4444", "31-45d"], ["#7f1d1d", "45+d"], ["#6b7280", "Done"],
];
const reportAgeBand = (ageDays) => {
  if (ageDays == null) { return "age-closed"; }
  if (ageDays <= 7) { return "age-green"; }
  if (ageDays <= 14) { return "age-lightgreen"; }
  if (ageDays <= 21) { return "age-amber"; }
  if (ageDays <= 30) { return "age-orange"; }
  if (ageDays <= 45) { return "age-red"; }
  return "age-darkred";
};
const isOpenReportStatus = (status) => ["new", "inprogress", "noaccess"].includes(status);
const reportTableAge = (row) => isOpenReportStatus(row.status) ? row.ageDays : null;
const formatReportDate = (value, options) => {
  if (!value) { return "—"; }
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = new Date(dateOnly ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString(undefined, options);
};
const formatReportPinDate = (value) => {
  if (!value) { return null; }
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = new Date(dateOnly ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? null : `${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getFullYear()).slice(-2)}`;
};
const reportStatusDate = (row) => row.status === "completed"
  ? ["Completed", row.completedAt]
  : row.status === "submitted" ? ["Submitted", row.submittedAt] : null;
function buildReportCsv(rows) {
  const cell = (value) => {
    let text = String(value ?? "");
    if (/^[\s]*[=+\-@]/.test(text)) { text = `'${text}`; }
    return `"${text.replace(/"/g, '""')}"`;
  };
  const value = (row, key) => {
    if (key === "location") { return `${row.city || ""}, ${row.state || ""}`; }
    if (key === "ageDays") { const age = reportTableAge(row); return age == null ? "—" : `${age}d`; }
    if (key === "appointment") { return formatReportDate(row.appointment); }
    return row[key];
  };
  return [REPORT_COLUMNS.map(([label]) => label), ...rows.map((row) => REPORT_COLUMNS.map(([, key]) => value(row, key)))]
    .map((line) => line.map(cell).join(",")).join("\r\n");
}

function buildReportPopup(row) {
  const content = document.createElement("div");
  content.className = "tw-space-y-1 tw-text-sm";
  const policyLink = document.createElement("a");
  policyLink.href = `/inspections/${encodeURIComponent(row.policyNo)}/process/pre_r`;
  policyLink.target = "_blank";
  policyLink.rel = "noopener noreferrer";
  policyLink.textContent = row.policyNo;
  const heading = document.createElement("strong");
  heading.appendChild(policyLink);
  content.appendChild(heading);
  const details = [
    ["Address", row.address],
    ["Location", `${row.city || ""}, ${row.state || ""} — ${row.county || ""}`],
    ["Inspector", row.inspector],
    ["Type", row.inspectionType],
    ["Status", row.prettyStatus || row.status],
    ["Policy Type", row.policyStatus],
    ["Age", row.ageDays == null ? "—" : `${row.ageDays}d`],
    ["Appointment", formatReportDate(row.appointment)],
  ];
  const statusDate = reportStatusDate(row);
  if (statusDate?.[1]) { details.push([statusDate[0], formatReportDate(statusDate[1])]); }
  details.forEach(([label, value]) => {
    const line = document.createElement("div");
    const name = document.createElement("strong");
    name.textContent = `${label}: `;
    line.appendChild(name);
    line.appendChild(document.createTextNode(value || "—"));
    content.appendChild(line);
  });
  return content;
}

export default function NaturalLanguageReportTab() {
  const [prompt, setPrompt] = useState("");
  const [report, setReport] = useState(null);
  const [reportTitle, setReportTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savedReports, setSavedReports] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [mapMode, setMapMode] = useState("age");
  const mapNode = useRef(null);
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const markersRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    apiReports.getSavedNaturalLanguageReports().then((response) => {
      if (!cancelled && response?.data?.status === 200) {
        setSavedReports(response.data.result || []);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!report || !mapNode.current || mapRef.current) { return undefined; }
    let cancelled = false;
    loadGoogle().then((google) => {
      if (!cancelled && mapNode.current) {
        const instance = new google.maps.Map(mapNode.current, {
          center: { lat: 28, lng: -82 }, zoom: 5, mapTypeControl: false,
          streetViewControl: false, scaleControl: true, mapId: "8041c9b566fb264",
        });
        mapRef.current = instance;
        setMap(instance);
      }
    }).catch(() => { if (!cancelled) { setError("Failed to load map."); } });
    return () => { cancelled = true; };
  }, [report]);

  useEffect(() => {
    let cancelled = false;
    if (!map || !window.google || !report) { return undefined; }
    markersRef.current.forEach((marker) => { marker.map = null; });
    markersRef.current = [];
    window.google.maps.importLibrary("marker").then(({ AdvancedMarkerElement }) => {
      if (cancelled) { return; }
      const bounds = new window.google.maps.LatLngBounds();
      const markers = report.rows.filter((row) => row.lat && row.lng).map((row) => {
        const tag = document.createElement("div");
        const statusDate = mapMode === "age" ? reportStatusDate(row) : null;
        const formattedStatusDate = statusDate?.[1] ? formatReportDate(statusDate[1]) : null;
        const pinDate = statusDate?.[1] ? formatReportPinDate(statusDate[1]) : null;
        tag.className = `inspection-map-tag inspection-map-tag-${statusDate ? "age-closed" : reportAgeBand(row.ageDays)}`;
        tag.style.fontSize = "0.75rem";
        tag.style.fontWeight = "700";
        tag.style.lineHeight = "2rem";
        if (pinDate) {
          tag.style.width = "max-content";
          tag.style.minWidth = "2.5rem";
          tag.style.padding = "0 0.35rem";
          tag.style.whiteSpace = "nowrap";
        }
        tag.textContent = pinDate || (mapMode === "age" ? row.ageDays == null ? "—" : String(row.ageDays) : row.inspectionType || "—");
        const pinTitle = formattedStatusDate
          ? `${statusDate[0]} ${formattedStatusDate}`
          : statusDate
            ? statusDate[0]
            : mapMode === "age"
              ? row.ageDays == null ? "Closed" : `${row.ageDays}d old`
              : row.inspectionType || "Unknown type";
        const marker = new AdvancedMarkerElement({ map, position: { lat: row.lat, lng: row.lng }, content: tag, title: `${row.policyNo} — ${pinTitle}` });
        const info = new window.google.maps.InfoWindow({ content: buildReportPopup(row) });
        marker.addListener("click", () => info.open({ anchor: marker, map }));
        bounds.extend(marker.position);
        return marker;
      });
      markersRef.current = markers;
      if (markers.length) {
        map.fitBounds(bounds);
        window.requestAnimationFrame(() => {
          if (!cancelled) {
            window.google.maps.event.trigger(map, "resize");
            map.fitBounds(bounds);
          }
        });
      }
    }).catch(() => setError("Failed to render report markers."));
    return () => {
      cancelled = true;
      markersRef.current.forEach((marker) => { marker.map = null; });
      markersRef.current = [];
    };
  }, [map, report, mapMode]);

  const saveReport = async () => {
    if (!report?.filters?.length || !reportTitle.trim()) { return; }
    setSaving(true); setError("");
    try {
      const response = await apiReports.saveNaturalLanguageReport({ title: reportTitle.trim(), filters: report.filters });
      if (response?.data?.status === 200) {
        setSavedReports((current) => [response.data.result, ...current].slice(0, 100));
      } else {
        setError(apiErrorMessage(response?.data, "Could not save this report."));
      }
    } catch (e) {
      setError(apiErrorMessage(e?.response?.data, e?.message || "Could not save this report."));
    } finally { setSaving(false); }
  };

  const runSavedReport = async (id) => {
    setLoading(true); setError(""); setReport(null); setReportTitle("");
    try {
      const response = await apiReports.runSavedNaturalLanguageReport(id);
      if (response?.data?.status === 200) {
        setReport(response.data.result);
        setReportTitle(response.data.result.title || "");
      }
      else { setError(apiErrorMessage(response?.data, "Could not run this saved report.")); }
    } catch (e) {
      setError(apiErrorMessage(e?.response?.data, e?.message || "Could not run this saved report."));
    } finally { setLoading(false); }
  };

  const runReport = async (event) => {
    event.preventDefault();
    if (!prompt.trim()) { return; }
    setLoading(true); setError(""); setReport(null); setReportTitle("");
    try {
      const response = await apiReports.generateNaturalLanguageReport(prompt.trim());
      if (response?.data?.status === 200) {
        setReport(response.data.result);
        setReportTitle(response.data.result.title || "");
      }
      else { setError(apiErrorMessage(response?.data, "Could not build this report.")); }
    } catch (e) {
      setError(apiErrorMessage(e?.response?.data, e?.message || "Could not build this report."));
    } finally { setLoading(false); }
  };

  const rows = report?.rows || [];
  const sortedRows = useMemo(() => {
    if (!sortKey) { return rows; }
    const value = (row) => sortKey === "location" ? `${row.city || ""}, ${row.state || ""}` : sortKey === "ageDays" ? reportTableAge(row) : row[sortKey] ?? "";
    const direction = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = value(a), bv = value(b);
      if (sortKey === "ageDays") {
        if (av == null) { return bv == null ? 0 : 1; }
        if (bv == null) { return -1; }
        return direction * (av - bv);
      }
      return direction * String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
    });
  }, [rows, sortKey, sortDir]);
  const toggleSort = (key) => {
    if (sortKey === key) { setSortDir((current) => current === "asc" ? "desc" : "asc"); }
    else { setSortKey(key); setSortDir("asc"); }
  };
  const downloadCsv = () => {
    if (!report) { return; }
    const blob = new Blob(["\uFEFF", buildReportCsv(sortedRows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${(reportTitle || "inspection-report").trim().replace(/[^a-z0-9_-]+/gi, "-").replace(/^-|-$/g, "") || "inspection-report"}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const mappedCount = rows.filter((row) => row.lat && row.lng).length;

  return <div className="tw-space-y-4">
    <div className="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-p-4">
      <h3 className="tw-font-semibold tw-text-lg">Ask about your inspections</h3>
      <form onSubmit={runReport} className="tw-mt-4">
        <label htmlFor="natural-report-prompt" className="tw-sr-only">Describe the inspections to report</label>
        <textarea id="natural-report-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={1000} rows={3} placeholder="e.g. Show open inspections in Florida assigned to Alex" className="tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-p-3 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary" />
        <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <button type="submit" disabled={loading || !prompt.trim()} className="tw-rounded-md tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white disabled:tw-opacity-50">{loading ? "Building report…" : "Build report"}</button>
          {EXAMPLES.map((example) => <button type="button" key={example} onClick={() => setPrompt(example)} className="tw-rounded-full tw-border tw-border-gray-300 tw-px-3 tw-py-1 tw-text-xs tw-text-gray-600 hover:tw-bg-gray-50">{example}</button>)}
        </div>
      </form>
      {savedReports.length > 0 && <div className="tw-mt-4 tw-border-t tw-border-gray-200 tw-pt-3">
        <p className="tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-500">Saved reports · shared with your company</p>
        <div className="tw-flex tw-flex-wrap tw-gap-2">{savedReports.map((saved) => <button type="button" key={saved.id} disabled={loading} onClick={() => runSavedReport(saved.id)} className="tw-rounded-full tw-border tw-border-gray-300 tw-px-3 tw-py-1 tw-text-xs hover:tw-bg-gray-50 disabled:tw-opacity-50">{saved.title}</button>)}</div>
      </div>}
      {error && <p role="alert" className="tw-mt-3 tw-text-sm tw-text-red-600">{error}</p>}
    </div>

    {report && <>
      <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
        <div className="tw-w-full tw-max-w-sm">
          <label htmlFor="natural-report-title" className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-500">Report name</label>
          <input id="natural-report-title" type="text" maxLength={120} value={reportTitle} onChange={(event) => setReportTitle(event.target.value)} className="tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-lg tw-font-semibold focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary" />
        </div>
        <span className="tw-flex-1 tw-pb-2 tw-text-sm tw-text-gray-600">{report.summary}</span>
        <button type="button" onClick={downloadCsv} disabled={!rows.length} className="tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm hover:tw-bg-gray-50 disabled:tw-opacity-50">Download CSV</button>
        <button type="button" onClick={saveReport} disabled={saving || !report.filters?.length || !reportTitle.trim()} className="tw-rounded-md tw-border tw-border-primary tw-px-3 tw-py-2 tw-text-sm tw-text-primary hover:tw-bg-blue-50 disabled:tw-opacity-50">{saving ? "Saving…" : "Save report"}</button>
      </div>
      <div className="tw-flex tw-flex-wrap tw-gap-2">
        {(report.filters || []).map((filter, index) => <span key={`${filter.field}-${index}`} className="tw-rounded-full tw-bg-blue-50 tw-px-3 tw-py-1 tw-text-xs tw-text-blue-800">{filter.op === "last_n_days" ? `${filter.field === "completed_at" ? "completed" : "created"} in the last ${filter.value} days` : `${filter.field.replace("_", " ")} ${filter.op === "in" ? "in" : filter.op} “${filter.values?.join(", ") || filter.value}”`}</span>)}
      </div>
    </>}
    {report && <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
      <span className="tw-text-sm tw-font-medium tw-text-gray-700">Map pins:</span>
      {[ ["age", "Age"], ["type", "Type"] ].map(([mode, label]) => <button key={mode} type="button" aria-pressed={mapMode === mode} onClick={() => setMapMode(mode)} className={`tw-rounded-md tw-border tw-px-3 tw-py-1 tw-text-sm ${mapMode === mode ? "tw-border-primary tw-bg-primary tw-text-white" : "tw-border-gray-300 tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50"}`}>{label}</button>)}
      <div role="group" aria-label="Age color key" className="tw-ml-auto tw-flex tw-flex-wrap tw-items-center tw-gap-3">
        <span className="tw-text-xs tw-font-medium tw-text-gray-600">Age:</span>
        {AGE_LEGEND.map(([color, label]) => <span key={label} className="tw-flex tw-items-center tw-gap-1">
          <span className="tw-inline-block tw-h-3 tw-w-3 tw-rounded" style={{ backgroundColor: color }} />
          <span className="tw-text-xs tw-text-gray-500">{label}</span>
        </span>)}
      </div>
    </div>}
    <div ref={mapNode} className="tw-w-full tw-rounded-lg tw-bg-gray-100" style={{ height: report ? 540 : 0, display: report ? "block" : "none" }} />
    {report && <div data-tour="nl-report-table" className="tw-overflow-x-auto">
      <p className="tw-mb-2 tw-text-sm tw-text-gray-600">Showing <strong>{rows.length}</strong>{rows.length === 500 ? " (maximum; narrow the request for more specific results)" : ""} inspections · {mappedCount} mapped</p>
      <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
        <thead className="tw-bg-fs-dark-blue tw-bg-opacity-25"><tr>{REPORT_COLUMNS.map(([label, key]) => <th key={key} aria-sort={sortKey === key ? sortDir === "asc" ? "ascending" : "descending" : "none"} className="tw-whitespace-nowrap tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">
          <button type="button" onClick={() => toggleSort(key)} className="tw-cursor-pointer tw-select-none hover:tw-text-primary">{label} {sortKey === key ? sortDir === "asc" ? "▲" : "▼" : ""}</button>
        </th>)}</tr></thead>
        <tbody className="tw-divide-y tw-divide-gray-200">{sortedRows.map((row, index) => <tr key={row.policyNo} className={`${index % 2 ? "tw-bg-gray-50" : "tw-bg-white"} hover:tw-bg-blue-50`}>
          <td className="tw-whitespace-nowrap tw-px-6 tw-py-2"><a className="tw-text-primary hover:tw-underline" target="_blank" rel="noopener noreferrer" href={`/inspections/${encodeURIComponent(row.policyNo)}/process/pre_r`}>{row.policyNo}</a></td>
          <td className="tw-px-6 tw-py-2">{row.address}</td><td className="tw-whitespace-nowrap tw-px-6 tw-py-2">{row.city}, {row.state}</td><td className="tw-whitespace-nowrap tw-px-6 tw-py-2">{reportTableAge(row) == null ? "—" : `${reportTableAge(row)}d`}</td><td className="tw-px-6 tw-py-2">{row.inspectionType || "—"}</td><td className="tw-whitespace-nowrap tw-px-6 tw-py-2">{formatReportDate(row.appointment)}</td><td className="tw-px-6 tw-py-2">{row.prettyStatus || row.status}</td><td className="tw-px-6 tw-py-2">{row.inspector || "—"}</td><td className="tw-px-6 tw-py-2">{row.policyStatus || "—"}</td>
        </tr>)}</tbody>
      </table>
      {rows.length === 0 && <p className="tw-py-6 tw-text-center tw-text-sm tw-text-gray-500">No inspections matched those filters.</p>}
    </div>}
  </div>;
}
